import { toast } from '@common/ui'
import { com } from '@eidu/web'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import getSemanticLinks from './api/analytics/getSemanticLinks'
import createSemanticLink from './api/analytics/createSemanticLink'
import deleteSemanticLink from './api/analytics/deleteSemanticLink'
import { auditLogKeys, semanticLinkKeys } from './queryKeys'
import SemanticLink = com.eidu.entity.analytics.SemanticLink
import SemanticLinkKey = com.eidu.entity.analytics.SemanticLinkKey
import SemanticLinkId = com.eidu.entity.analytics.SemanticLinkId
import EntityPath = com.eidu.entity.path.EntityPath
import semanticLinkIdFromString = com.eidu.entity.analytics.semanticLinkIdFromString

const useSemanticLinks = () => {
  const query = useQuery({
    queryKey: semanticLinkKeys.list(),
    queryFn: getSemanticLinks,
  })
  return {
    semanticLinks: query.data,
    isLoading: query.isLoading,
    error: query.error,
  }
}

export const useCreateSemanticLink = (onSuccess?: () => void) => {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ key, path }: { key: SemanticLinkKey; path: EntityPath }) => {
      const newId = semanticLinkIdFromString(crypto.randomUUID())
      return createSemanticLink({ semanticLink: new SemanticLink(newId, key, path) })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: semanticLinkKeys.all })
      queryClient.invalidateQueries({ queryKey: auditLogKeys.all })
      toast.positive('Successfully created semantic link')
      onSuccess?.()
    },
    onError: (error) => {
      toast.negative(`Error creating semantic link: ${error instanceof Error ? error.message : String(error)}`)
    },
  })
  const save = (key: SemanticLinkKey, path: EntityPath) => mutateAsync({ key, path })
  return { save, isSaving: isPending }
}

export const useDeleteSemanticLink = () => {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (semanticLinkId: SemanticLinkId) => deleteSemanticLink({ semanticLinkId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: semanticLinkKeys.all })
      queryClient.invalidateQueries({ queryKey: auditLogKeys.all })
      toast.positive('Successfully deleted semantic link')
    },
    onError: (error) => {
      toast.negative('Error deleting semantic link')
      console.error(error)
    },
  })
  return { delete: mutateAsync, isDeleting: isPending }
}

export default useSemanticLinks
